import { InfoCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import posthog from '@leyan/lytics';
import { useMemoizedFn, useMount } from 'ahooks';
import { Affix, Button, Checkbox, message, Pagination, Space, Spin, Tabs, Tooltip } from 'antd';
import Empty from 'components/Empty';
import useModal from 'hooks/useModal';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { CancelOrder, handleBuyerCancellation } from 'services/api/refund';
import toLowerCase from 'utils/toLowerCase';
import CancelListItem from '../components/CancelListItem';
import Filter, { FilterChangeEvent } from '../components/OrderFilter/Filter';
import RefundDrawer from '../components/RefundDrawer';
import useCancelList from '../hooks/useCancelList';
import useFilter from '../hooks/useFilter';
import { usePlatform } from '../hooks/usePlatformList';
import { getCount } from '../utils/count';
import { useTransAppProductName } from 'hooks/useTrans';
import { EXPORT_CANCEL_ORDER_PLATFORMS, FilterIdType } from '../utils/constants';
import { CancelExportButton } from '../components/CancelExportButton';
import { useIsLightChatLayout } from 'hooks/useChatLayoutType';
import { OrderDrawer } from 'components/LightConversation/components/OrderDrawer';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { uniqBy } from 'lodash-es';
import StatusButton from 'components/StatusButton';
import { PopConfrimContent } from '../components/CancelApprovalBtns';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';

const Container = styled.div`
  .ant-tabs {
    background-color: var(--fett-bg-panel);
  }
  .ant-tabs-nav {
    flex: 0 0 auto;
  }
  .ant-pagination {
    text-align: right;
  }
  > .ant-spin-nested-loading {
    flex: 1 0 0;
    padding: 0 16px 16px;

    > .ant-spin-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      > div {
        flex: 1 0 0;
      }
      > footer {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        position: sticky;
        bottom: 0;
        margin: 16px 0 -16px;
        padding: 16px 0;
        background: var(--fett-bg-panel);
        .ant-pagination-jump-next + .ant-pagination-item {
          display: none;
        }
        > div {
          > * {
            margin-right: 14px;
          }
        }
      }
    }
  }
`;

const Table = styled.table`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--fett-bg-panel);
  overflow: hidden;

  tr {
    display: flex;
    overflow: hidden;
  }
  td {
    padding: 0 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-grow: 1;
    box-sizing: border-box;
  }
  .td-title {
    width: 26%;
  }
  .td-amount {
    width: 12%;
    text-align: center;
  }
  .td-reason {
    width: 22%;
  }
  .td-status {
    width: 22%;
  }
  .td-action {
    width: 18%;
  }
`;

const CancelList = React.memo(() => {
  const platform = usePlatform()!;
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  const { query, onQueryChange } = useFilter('cancel');
  const { update, visible, hide, meta } = useModal(false);
  const {
    list = [],
    statusFilter,
    conversationList = [],
    setStatusFilter,
    loading,
    statusCount,
    pagination: { pageSize, current, onChange, total },
    refresh,
  } = useCancelList(query, platform);
  const modalMetaRef = useRef<{
    row: CancelOrder;
    conversation?: Conversation.Conversation;
  } | null>(null);
  const isLightLayout = useIsLightChatLayout();

  const handleFilterChange = useMemoizedFn((e: FilterChangeEvent) => {
    const { snType, sn } = e;
    const { orderSn, returnSn, returnTrackingNumber } = query;
    if (
      (snType === FilterIdType.orderId && sn !== orderSn) ||
      (snType === FilterIdType.returnId && sn !== returnSn) ||
      (snType === FilterIdType.returnTrackingNumber && sn !== returnTrackingNumber)
    ) {
      setStatusFilter('ALL');
    }

    onQueryChange(e);
  });

  useEffect(() => {
    if (visible && modalMetaRef.current?.row) {
      const {
        current: { row },
      } = modalMetaRef;
      const nextRow = list.find((it) => it.orderId === row.orderId);
      modalMetaRef.current.row = nextRow || row;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);
  useMount(() => {
    posthog.capture('$cancelOrder', {
      type: 'entryPage',
    });
  });

  let detailDrawer = null;
  if (modalMetaRef.current?.row) {
    const { row, conversation } = modalMetaRef.current;
    if (isLightLayout && conversation) {
      detailDrawer = (
        <OrderDrawer
          visible={visible}
          conversation={conversation}
          sendable={false}
          onClose={() => hide()}
          entryType="cancel"
          cancelOrder={row}
          defaultActiveTab="cancelDetail"
          contactEnable
        />
      );
    } else {
      detailDrawer = (
        <RefundDrawer
          platform={toLowerCase(platform)}
          visible={visible}
          onClose={() => hide()}
          onUpdate={refresh}
          key={`${meta}`}
          type="cancel"
          row={row}
          conversation={conversation}
        />
      );
    }
  }

  const [selectedRows, setSelectedRows] = useState<CancelOrder[]>([]);

  const getPermission = useGetPermission();

  const selected = useCallback(
    (row: CancelOrder) => selectedRows.some((_) => _.internalTradeId === row.internalTradeId),
    [selectedRows],
  );

  const { acceptableList, allChecked, indeterminate } = useMemo(() => {
    const acceptableList = list.filter(({ region, storeId, operate }, index) => {
      const operatable = getPermission('操作弃单', { platform, region, storeId: Number(storeId) });
      return (
        operatable &&
        (platform === 'SHOPEE'
          ? operate.includes('AGREE')
          : isTiKTokUpperCase(platform)
          ? operate.length > 0
          : false) &&
        conversationList[index]
      );
    });
    const allChecked = acceptableList.every(selected);
    const indeterminate = !allChecked && acceptableList.some(selected);
    return {
      acceptableList,
      allChecked,
      indeterminate,
    };
  }, [list, getPermission, conversationList, platform, selected]);

  return (
    <>
      <Filter filterType="cancel" onChange={handleFilterChange} />
      <Container className="flex flex-grow flex-shrink-0 basis-0 flex-col bg-[var(--fett-bg-panel)] mx-4 my-4">
        <Affix target={() => document.querySelector<HTMLDivElement>('.refund-container')}>
          <Tabs
            activeKey={statusFilter}
            onChange={(key: any) => setStatusFilter(key)}
            tabBarExtraContent={
              EXPORT_CANCEL_ORDER_PLATFORMS.includes(platform) ? (
                <div className="flex px-4 gap-4">
                  <CancelExportButton
                    query={query}
                    statusFilter={statusFilter}
                    platform={platform}
                  />
                </div>
              ) : null
            }
          >
            <Tabs.TabPane
              tab={`${t('scene.orderWorkspace.cancelOrderTab.tab.all')}(${getCount(
                statusCount?.total,
              )})`}
              key="ALL"
            />
            <Tabs.TabPane
              tab={`${t('scene.orderWorkspace.cancelOrderTab.tab.pending')}(${getCount(
                statusCount?.pendingCount,
              )})`}
              key="PENDING"
            />
            <Tabs.TabPane
              key="PROCESS"
              tab={
                <Space>
                  {`${t('scene.orderWorkspace.cancelOrderTab.tab.processing')}(${getCount(
                    statusCount?.processedCount,
                  )})`}
                  <Tooltip title={appProductNameT('ViewRefund.cancelPendingTabTips')}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
            />
            <Tabs.TabPane
              tab={`${t('scene.orderWorkspace.cancelOrderTab.tab.completed')}(${getCount(
                statusCount?.completeCount,
              )})`}
              key="COMPLETE"
            />
          </Tabs>
        </Affix>

        <Spin spinning={loading}>
          <Table>
            <thead className="bg-[var(--fett-erp-card-bg)] h-[56px] leading-[56px] text-[var(--fett-text-primary)] font-medium">
              <tr>
                {acceptableList.length > 0 && (
                  <td className="w-[50px] ml-1">
                    <Checkbox
                      checked={allChecked}
                      indeterminate={indeterminate}
                      onChange={({ target: { checked } }) =>
                        setSelectedRows(
                          checked
                            ? uniqBy([...selectedRows, ...acceptableList], 'internalTradeId')
                            : selectedRows.filter((_) =>
                                acceptableList.every(
                                  (__) => __.internalTradeId !== _.internalTradeId,
                                ),
                              ),
                        )
                      }
                    />
                  </td>
                )}
                <td className="td-title">
                  {t('scene.orderWorkspace.returnRefundTab.list.column.product')}
                </td>
                <td className="td-amount">
                  {t('scene.orderWorkspace.returnRefundTab.list.column.amount')}
                </td>
                <td className="td-reason">
                  {t('scene.orderWorkspace.returnRefundTab.list.column.cancelReason')}
                </td>
                <td className="td-status">
                  {t('scene.orderWorkspace.returnRefundTab.list.column.status')}
                </td>
                <td className="td-action">
                  {t('scene.orderWorkspace.returnRefundTab.list.column.action')}
                </td>
              </tr>
            </thead>
            <tbody>
              {list.map((row, idx) => (
                <CancelListItem
                  row={row}
                  conversation={conversationList[idx]}
                  platform={platform}
                  key={row.orderId + platform + String(idx) + row.orderState}
                  refreshList={refresh}
                  onClick={() => {
                    modalMetaRef.current = { row, conversation: conversationList[idx] };
                    update(true, `${row.orderId}-${idx}`);
                  }}
                  checkbox={
                    acceptableList.length > 0 && (
                      <Checkbox
                        disabled={acceptableList.every(
                          (_) => _.internalTradeId !== row.internalTradeId,
                        )}
                        checked={selected(row)}
                        onChange={({ target: { checked } }) =>
                          setSelectedRows(
                            checked
                              ? [...selectedRows, row]
                              : selectedRows.filter(
                                  (_) => _.internalTradeId !== row.internalTradeId,
                                ),
                          )
                        }
                      />
                    )
                  }
                />
              ))}
              {!list?.length && !loading && <Empty />}
            </tbody>
          </Table>
          <footer>
            {selectedRows.length > 0 ? (
              <div>
                <StatusButton
                  danger
                  popConfirmProps={
                    platform === 'SHOPEE'
                      ? {
                          title: (
                            <PopConfrimContent>
                              {t('scene.orderWorkspace.cancelOrderTab.dialog.acceptConfirm')}
                            </PopConfrimContent>
                          ),
                        }
                      : undefined
                  }
                  onClick={() => {
                    const hideLoading = message.loading(t('处理中'), 0);
                    return selectedRows
                      .reduce<Promise<any>>((res, { externalStoreId, orderId }) => {
                        return res.then(() => {
                          return handleBuyerCancellation({
                            externalStoreId,
                            orderId,
                            platform,
                            isAccept: true,
                          }).catch(() => {});
                        });
                      }, Promise.resolve())
                      .then(() => {
                        hideLoading();
                        message.success(t('已完成'));
                        setSelectedRows([]);
                        refresh();
                      });
                  }}
                >
                  {t('scene.orderWorkspace.cancelOrderTab.list.action.accept')}
                </StatusButton>
                {t('voucher.已选x', { x: selectedRows.length })}
                <Button type="text" onClick={() => setSelectedRows([])}>
                  {t('清空')}
                </Button>
              </div>
            ) : (
              <div />
            )}
            <Pagination
              pageSize={pageSize}
              current={current}
              onChange={onChange}
              total={total}
              showSizeChanger
              pageSizeOptions={['10', '20', '100']}
            />
          </footer>
        </Spin>
      </Container>
      {detailDrawer}
    </>
  );
});

export default CancelList;
